import React, { useState, useEffect, useCallback, useMemo } from "react";

import {
  Pane,
  Paragraph,
  TextInput,
  Button,
  IconButton,
  Checkbox,
  Combobox,
  SelectMenu,
  AddIcon,
  Badge,
  ChevronDownIcon,
  Table,
  CrossIcon,
} from 'evergreen-ui';

export const ForeignKeyDropDownInput = props => {

  const onixTypes = [
    "format",
    "format__description",
    "formatDetail",
    "status",
    "status__description",
    "audience",
    "onixContributorCode"
  ];

  const isOnix = onixTypes.indexOf(props.el) > -1 ? true : false;
  const makeItemList = useCallback(arr => {
    if (!Array.isArray(arr)) {
      console.warn("Expected an array but received:", arr);
      return []; // Return an empty array if arr is not an array
    }
    const newArr = []; // Add empty option at beginning
    arr.forEach((item, idx) => {
      if (item && item.id) { // Only process items with valid IDs
        let newItem = {
          id: item.id,
          key: `${props.el}-${item.id}-${idx}`,
          value: item.value || '',
          label: ''
        };

        if (isOnix) {
          newItem.label = `${item.description}`;
          newItem.value = item.description;
        } else if (props.el === "creator") {
          newItem.label = `${item.displayName} (id: ${item.id})`;
          newItem.value = item.displayName;
        } else if (props.el === "region") {
          newItem.label = item.name || '';
          newItem.value = item.name || '';
        } else {
          let value = item.value === "" ? "None" : item.value;
          newItem.label = value;
          newItem.value = value;
        }
        newArr.push(newItem);
      }
    });
    return newArr;
  }, [isOnix, props.el]);

  const [items, setItems] = useState([]);
  const [newmode, setNewmode] = useState(false);
  const [newval, setNewval] = useState("");
  const [currentobj, setCurrentobj] = useState({ label: "" });
  const [selectItemFlag, setSelectItemFlag] = useState(false);
  const [selectedItemsState, setSelectedItems] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [exclude, setExclude] = useState(false);

  const handleChange = item => {
    if (item && typeof item === 'object') {
      delete item.label;
      props.handleChange(item);
      setSelectItemFlag(true);
    }
  }

  const getObj = selected => {
    if (!selected) return null;

    if (props.el === "onixContributorCode" || props.el === "creator") {
      return { key: props.el, val: selected, id: props.creatorId };
    } else if (props.el === "region") {
      console.log("Region selected:", selected);
      return { key: props.el, val: selected };
    } else {
      return { key: props.el, val: selected };
    }
  }

  const shouldShowLabel = (props.opts && props.opts.hasOwnProperty("showLabel")) ? props.opts.showLabel : true;

  const getValWithLabel = useCallback(() => {
    if (selectItemFlag === false) {
      const defaultItem = makeItemList(props.values)[0]
      if (defaultItem) {
        // Return the item without triggering state updates
        return makeItemList(props.values)[0];
      } else {
        return makeItemList([props.value])[0];
      }
    } else {
      return makeItemList([props.value])[0];
    }
  }, [selectItemFlag, makeItemList, props.values, props.value]);

  // Handle default item selection through an effect instead of during render
  useEffect(() => {
    if (selectItemFlag === false && props.values && props.values.length > 0) {
      const defaultItem = makeItemList(props.values)[0];
      if (defaultItem) {
        setSelectItemFlag(true);
      }
    }
    // Only run this when component mounts or when values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.values, selectItemFlag]);

  const handleNewItem = val => {
    props.handleAddNew(newval);
    setNewval("");
    setNewmode(false);
  }


  const getCurrent = useCallback(() => {
    // Don't call getValWithLabel if we don't have the right props
    //console.log(`>>> ForeignKeyDropDownInput (${props.el}) - getCurrent called. props.value:`, props.value); // LOG PROPS.VALUE
    if (!props.value || !props.values) {
      //console.log(`<<< ForeignKeyDropDownInput (${props.el}) - getCurrent returning '' (no props.value or props.values)`); // LOG RETURN
      return '';
    }

    let displayValue = '';
    // Use the props.value directly
    if (isOnix) {
      displayValue = props.value?.description || '';
    } else if (props.el === "creator") {
      displayValue = props.value?.displayName || '';
    } else if (props.el === "category") {
      displayValue = props.value?.value || '';
    } else if (props.el === "region") { // Add specific check for region
      displayValue = props.value?.name || ''; // Use name property for region
    } else {
      displayValue = props.value?.value || '';
    }
    //console.log(`<<< ForeignKeyDropDownInput (${props.el}) - getCurrent returning displayValue: '${displayValue}'`); // LOG RETURN
    return displayValue;

  }, [isOnix, props.el, props.value, props.values]);

  // Create a memoized selected item to use in the Combobox
  const selectedItem = useMemo(() => {
    // New logic: Directly find the item based on props.value.id
    if (props.value?.id && items && items.length > 0) {
      const foundItem = items.find(item => item.id === props.value.id);
      // Ensure the found item has the expected structure for Combobox
      if (foundItem && typeof foundItem.label !== 'undefined') {
         //console.log(`ForeignKeyDropDownInput (${props.el}) - Found selectedItem:`, foundItem);
         return foundItem;
      }
    }
    // Fallback if no value, no items, or item not found
    //console.log(`ForeignKeyDropDownInput (${props.el}) - Falling back to default selectedItem`);
    return { label: "Select...", value: null };
  }, [props.value, items]); // Depends on props.value and the generated items list

  const mandatory = props.opts && props.opts.mandatory && props.editing && !getCurrent()
    ? true
    : false;

  // Effect to update the items list when props.values changes
  useEffect(() => {
    if (props.values) {
      const newItems = makeItemList(props.values);
      // Optimization: Only set items if they have actually changed
      // This requires comparing arrays, which can be complex. For now, rely on parent memoization if possible.
      // A simpler check could compare lengths or stringified versions if performance becomes an issue.
      if (props.el === 'region') { // Log specifically for region
        console.log(`>>> ForeignKeyDropDownInput (region) - useEffect setting items:`, newItems);
      }
      setItems(newItems);
    }
  }, [props.values, makeItemList]);

  const handleCheck = (checked) => {
    props.setDetails({ ...props.details, [props.formLabel]: { exclude: checked, labels: selectedLabels } })
    handleChange(selectedItemsState, checked)
    setExclude(checked)
  }

  const handleSelect = (item) => {
    const selected = props.opts?.multiSelect ? [...selectedItemsState, item.value] : [item.value];
    const selectedItems = selected;
    const labels = props.opts?.multiSelect ? [...selectedLabels, item.label] : [item.label];
    setSelectedItems(selectedItems);
    setSelectedLabels(labels);
    if (props.setDetails) {
      props.setDetails({ ...props.details, [props.formLabel]: { exclude: exclude, labels: labels } });
    }
    handleChange(selectedItems, exclude);
  }

  const handleDeselect = (item) => {
    if (!props.opts?.multiSelect) return;
    const deselectedItemIndex = selectedItemsState.indexOf(item.value);
    const selectedItems = selectedItemsState.filter((_item, i) => i !== deselectedItemIndex);
    const labels = selectedLabels.filter(label => label !== item.label);
    setSelectedItems(selectedItems);
    setSelectedLabels(labels);
    if (props.setDetails) {
      props.setDetails({ ...props.details, [props.formLabel]: { exclude: exclude, labels: labels } });
    }
    handleChange(selectedItems, exclude);
  }

  return (
    <Pane
      width={props.width}
      marginBottom={shouldShowLabel ? props.opts?.short ? 0 : 16 : 0}
      marginRight={(props.opts && props.opts.hasOwnProperty("marginRight") ? props.opts.marginRight : 0)}
      key={`${props.el.replace("\s", "")}`}
      display={props.opts?.badge ? "flex" : "block"}
      alignItems={props.opts?.badge ? "center" : "flex-start"}

    >
      <Pane display="flex">
        {shouldShowLabel && (
          <Paragraph marginBottom={props.opts?.badge || props.opts?.short ? 0 : 8}
            marginRight={props.opts?.badge ? 4 : 0}
            size={300}
            color="#667b7f">{props.formLabel}</Paragraph>

        )}

        {mandatory && (
          <Paragraph color="red" size={400} marginLeft={4}>*</Paragraph>
        )}
      </Pane>
      {props.editing ? (
        <Pane>
          {props.opts?.multiSelect ? (
            <Pane
              marginRight={(props.marginRight ? props.marginRight : 0)}
              key={`${props.el.replace("\s", "")}`}
            >
              <SelectMenu
                titleView={({ close, title, headerHeight }) => {
                  return (
                    <Pane
                      display="flex"
                      alignItems="center"
                      borderBottom="default"
                      padding={8}
                      height={headerHeight}
                      boxSizing="border-box"
                    >
                      <Checkbox label="Exclude Selected?" checked={exclude} onChange={e => handleCheck(e.target.checked)} margin={0} />
                    </Pane>
                  )
                }}
                isMultiSelect={true}
                options={items}
                selected={selectedItemsState}
                onSelect={handleSelect}
                onDeselect={handleDeselect}
              >
                <Button iconAfter={ChevronDownIcon} marginTop={4}
                  className={selectedItemsState.length > 0 ? exclude ? "exclude-button" : "include-button" : ""} >
                  {props.formLabel} ({selectedItemsState.length})
                </Button>
              </SelectMenu>
            </Pane>
          ) : (
            <Pane display="flex" flexDirection="row" alignItems="center" marginBottom={8}>
              <Combobox
                key={getCurrent() ? `${getCurrent()}-${props.el}-picker` : `${props.el}-picker`}
                initialSelectedItem={{ label: "Select...", value: null }}
                items={items}
                itemToString={item => {
                  //console.log("itemToString called with:", item);
                  return item?.label || '';
                }}
                onChange={selected => handleChange(getObj(selected))}
                marginBottom={shouldShowLabel ? props.opts?.badge || props.opts?.new ? 0 : 8 : 0}
                width={props.opts?.new ? props.width - 90 : props.width}
                height={props.opts?.new ? props.opts?.short ? 20 : 28 : 32}
                selectedItem={selectedItem}
              />
              {/* {console.log("=>>>> = newmode = >>>>=", getValWithLabel())} */}

              {!newmode && props.opts?.new && props.el !== "salesperson" && props.el !== "paymentTerm" && props.el !== "shippingType" && props.el !== "orderStatus" && props.el !== 'paymentType' && <Button size="small" marginLeft={4} onClick={e => setNewmode(true)}>Add New</Button>}

            </Pane>
          )}
          {newmode && props.opts?.new && (
            <Pane display="flex" flexDirection="row" alignItems="center">
              <TextInput
                height={24}
                className="item-form"
                value={newval}
                width={props.width - 50}
                onChange={e => setNewval(e.target.value)}
              />
              <Pane display="flex">
                <IconButton size="small" disabled={!newval} icon={AddIcon} intent="success" marginLeft={8} onClick={e => handleNewItem(newval)} />
                <IconButton size="small" icon={CrossIcon} intent="danger" marginLeft={8} onClick={e => setNewmode(false)} />
              </Pane>
            </Pane>
          )}
        </Pane>
      ) : props.opts?.badge ? (<Badge>{getCurrent()}</Badge>
      ) : (
        <TextInput
          height={props.opts?.short ? 20 : 32}
          className="item-form"
          value={String(getCurrent() || '')}
          disabled={true}
          width={props.width}
        />
      )}
    </Pane>
  );
}

export const ForeignKeyMultiSelectDropDownInput = props => {
  const [items, setItems] = useState([]);
  const [exclude, setExclude] = useState(false);
  const [selectedItemsState, setSelectedItems] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const handleChange = useCallback((selectedItems, exclude) => {
    console.log('ForeignKeyMultiSelectDropDownInput handleChange:', {
      selectedItems,
      exclude,
      field: props.field
    });

    // Convert selected values (IDs) back to full product objects
    const selectedProducts = selectedItems.map(id => {
      const item = items.find(i => i.id === id);
      return item ? {
        id: item.id,
        value: item.label
      } : null;
    }).filter(Boolean);

    console.log('Selected products:', selectedProducts);

    props.handleChange({
      key: props.field,
      val: selectedProducts,
      exclude: exclude
    });
  }, [props.field, props.handleChange, items]);

  const handleCheck = useCallback((checked) => {
    setExclude(checked);
    handleChange(selectedItemsState, checked);
  }, [handleChange, selectedItemsState]);

  // Transform items into SelectMenu options
  const getOptions = useCallback((items) => {
    return items.map((item, idx) => ({
      id: item.id,
      label: item.label,
      value: item.value || item.label,
      key: `option-${item.id}-${idx}`
    }));
  }, []);

  useEffect(() => {
    if (props.values) {
      const validItems = props.values
        .filter(item => item && item.id && (item.value || item.title))
        .map((item, idx) => ({
          id: item.id.toString(),
          value: item.id.toString(), // Use ID as value for proper selection tracking
          label: item.label || (item.title ? `${item.title} ${item.format && item.format.description ? `(${item.format.description})` : ''} ${item.isbn ? `(${item.isbn})` : ''}` : item.value),
          key: `item-${item.id}-${idx}`
        }));

      setItems(validItems);
    }
  }, [props.values]);

  useEffect(() => {
    if (props.value === "") {
      setSelectedItems([]);
      setSelectedLabels([]);
      setExclude(false);
    }
  }, [props.value]);

  return (
    <Pane marginRight={props.marginRight || 0}>
      <SelectMenu
        isMultiSelect
        title={props.formLabel}
        options={getOptions(items)}
        selected={selectedItemsState}
        onSelect={(item) => {
          if (!item) return;
          console.log('SelectMenu onSelect:', item);
          const selectedItems = [...selectedItemsState, item.value];
          const labels = [...selectedLabels, item.label];
          setSelectedItems(selectedItems);
          setSelectedLabels(labels);
          props.setDetails?.({
            ...props.details,
            [props.formLabel]: { exclude, labels }
          });
          handleChange(selectedItems, exclude);
        }}
        onDeselect={(item) => {
          if (!item) return;
          console.log('SelectMenu onDeselect:', item);
          const deselectedItemIndex = selectedItemsState.indexOf(item.value);
          const selectedItems = selectedItemsState.filter((_, i) => i !== deselectedItemIndex);
          const labels = selectedLabels.filter(label => label !== item.label);
          setSelectedItems(selectedItems);
          setSelectedLabels(labels);
          props.setDetails?.({
            ...props.details,
            [props.formLabel]: { exclude, labels }
          });
          handleChange(selectedItems, exclude);
        }}
        hasFilter={true}
        filterPlaceholder="Search..."
        width={props.width || 300}
        css={{
          '.evergreen-select-menu-search input': {
            WebkitFontSmoothing: 'antialiased'
          }
        }}
      >
        <Button
          iconAfter={ChevronDownIcon}
          marginTop={4}
          className={selectedItemsState.length > 0 ? exclude ? "exclude-button" : "include-button" : ""}
        >
          {props.formLabel} ({selectedItemsState.length})
        </Button>
      </SelectMenu>
      {exclude && selectedItemsState.length > 0 && (
        <Badge color="red" marginLeft={8}>
          Excluding {selectedItemsState.length} items
        </Badge>
      )}
    </Pane>
  );
};

export const AddNewForeignKey = props => {
  const [items, setItems] = useState([]);
  const [newmode, setNewmode] = useState(false);
  const [newval, setNewval] = useState("");
  //console.log("set items key", items)

  const handleNewItem = val => {
    props.handleAddNew(newval, items, props.el);
    setNewval("");
    setNewmode(false);
  }

  useEffect(() => {
    if (props.values) {
      setItems(props.values);
    }
  }, [props.values]);

  return (
    <Pane margin={16}>
      <Paragraph marginBottom={8} size={300} color="grey"> {props.formLabel} ({items.length})
        {!newmode &&
          <Button marginLeft={8} size="small" intent="success" onClick={() => setNewmode(true)}>Add New</Button>
        }
      </Paragraph>
      {newmode && (
        <Pane display="flex" flexDirection="row" alignItems="center">
          <TextInput
            height={24}
            className="item-form"
            value={newval}
            width={props.width - 50}
            onChange={e => setNewval(e.target.value)}
          />
          <IconButton size="small" disabled={!(newval.length > 0)} icon={AddIcon} intent="success" appearance="primary" marginLeft={8} onClick={() => handleNewItem(newval)} />
          <IconButton size="small" icon={CrossIcon} intent="danger" marginLeft={8} onClick={() => { setNewmode(false); setNewval("") }} />
        </Pane>
      )}
      <Pane maxHeight={150} overflowY="scroll">
        {items.map((item, index) => (
          <Table.Row key={item.id || `${item.value}-${index}`} height={28} backgroundColor={index % 2 === 0 ? "#f9faff" : "#F4F5F9"}>
            <Table.TextCell>
              {item.value}
            </Table.TextCell>
          </Table.Row>
        ))}
      </Pane>
    </Pane>
  );
}
